"use client";

import { c as _c } from "react/compiler-runtime";
import Modal from "@/components/modal";
import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/routing";
import { BotIcon, BrainCircuitIcon, CalendarIcon, FileTextIcon, GlobeIcon, MailIcon, MonitorSmartphoneIcon, SparklesIcon, TrendingUpIcon, WorkflowIcon } from "lucide-react";
import * as motion from "motion/react-client";
import { useLocale, useTranslations } from "next-intl";
import ContactForm from "./_components/contact-form";
import Footer from "./_components/footer";
import Header from "./_components/header";
import Hero from "./_components/hero";
import Projects from "./_components/projects";
import Testimonials from "./_components/testimonials";
const aiExamples = [{
  key: "chatbot",
  icon: BotIcon
}, {
  key: "documents",
  icon: FileTextIcon
}, {
  key: "analytics",
  icon: TrendingUpIcon
}, {
  key: "workflows",
  icon: WorkflowIcon
}, {
  key: "content",
  icon: SparklesIcon
}, {
  key: "integration",
  icon: BrainCircuitIcon
}];
const springHover = {
  type: "spring" as const,
  stiffness: 300,
  damping: 20
};
export default function HomePage() {
  const $ = _c(196);
  const t = useTranslations("Home");
  useLocale();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Header />;
    t1 = <Hero />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="absolute top-0 left-0 right-0 section-divider" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <motion.div className="absolute -right-32 top-0 size-96 rounded-full bg-[hsl(var(--accent))]/4 blur-[120px]" animate={{
      scale: [1, 1.1, 1],
      x: [0, 15, 0]
    }} transition={{
      duration: 12,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      translateY: 40,
      opacity: 0
    };
    t5 = {
      translateY: 0,
      opacity: 1
    };
    t6 = {
      once: true,
      margin: "-80px"
    };
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      duration: 0.7,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t) {
    t8 = t("sections.about.title");
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <SectionLabel>{t8}</SectionLabel>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    let t11;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = new Date().getFullYear();
      $[14] = t11;
    } else {
      t11 = $[14];
    }
    t10 = t.rich("sections.about.content", {
      yearsOfExperience: t11 - 2010,
      strong: _temp,
      p: _temp2
    });
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[15] !== t10) {
    t11 = <div className="mt-6 text-base text-muted-foreground leading-[1.8] flex flex-col gap-4">{t10}</div>;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t9) {
    t12 = <motion.div initial={t4} whileInView={t5} viewport={t6} transition={t7}>{t9}{t11}</motion.div>;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  let t14;
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      translateY: 30,
      opacity: 0
    };
    t14 = {
      translateY: 0,
      opacity: 1
    };
    t15 = {
      once: true,
      margin: "-80px"
    };
    $[20] = t13;
    $[21] = t14;
    $[22] = t15;
  } else {
    t13 = $[20];
    t14 = $[21];
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t) {
    t17 = t("sections.dev.title");
    $[24] = t;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t17) {
    t18 = <motion.div initial={t13} whileInView={t14} viewport={t15} transition={t16}><SectionLabel>{t17}</SectionLabel></motion.div>;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      translateY: 25,
      opacity: 0
    };
    t20 = {
      translateY: 0,
      opacity: 1
    };
    t21 = {
      once: true,
      margin: "-30px"
    };
    t22 = {
      duration: 0.5,
      delay: 0.1,
      ease: "easeOut"
    };
    t23 = {
      translateY: -4
    };
    $[28] = t19;
    $[29] = t20;
    $[30] = t21;
    $[31] = t22;
    $[32] = t23;
  } else {
    t19 = $[28];
    t20 = $[29];
    t21 = $[30];
    t22 = $[31];
    t23 = $[32];
  }
  let t24;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      rotate: 5,
      scale: 1.1
    };
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <motion.div className="size-10 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center text-[hsl(var(--accent))] transition-colors duration-300 group-hover:bg-[hsl(var(--accent))]/15" whileHover={t24} transition={springHover}><MonitorSmartphoneIcon className="size-5" /></motion.div>;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  let t26;
  if ($[35] !== t) {
    t26 = t("sections.dev.mobile.title" as any);
    $[35] = t;
    $[36] = t26;
  } else {
    t26 = $[36];
  }
  let t27;
  if ($[37] !== t26) {
    t27 = <div className="flex items-center gap-3 mb-3">{t25}<h3 className="font-semibold text-foreground text-lg">{t26}</h3></div>;
    $[37] = t26;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== t) {
    t28 = t("sections.dev.mobile.description" as any);
    $[39] = t;
    $[40] = t28;
  } else {
    t28 = $[40];
  }
  let t29;
  if ($[41] !== t28) {
    t29 = <p className="text-sm text-muted-foreground leading-relaxed">{t28}</p>;
    $[41] = t28;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] !== t27 || $[44] !== t29) {
    t30 = <motion.div initial={t19} whileInView={t20} viewport={t21} transition={t22} whileHover={t23} className="group bg-card border border-border rounded-xl p-6 transition-all duration-300 hover:border-[hsl(var(--accent))]/20 hover:glow-accent">{t27}{t29}</motion.div>;
    $[43] = t27;
    $[44] = t29;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      translateY: 25,
      opacity: 0
    };
    t32 = {
      translateY: 0,
      opacity: 1
    };
    t33 = {
      once: true,
      margin: "-30px"
    };
    t34 = {
      duration: 0.5,
      delay: 0.2,
      ease: "easeOut"
    };
    t35 = {
      translateY: -4
    };
    $[46] = t31;
    $[47] = t32;
    $[48] = t33;
    $[49] = t34;
    $[50] = t35;
  } else {
    t31 = $[46];
    t32 = $[47];
    t33 = $[48];
    t34 = $[49];
    t35 = $[50];
  }
  let t36;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      rotate: -5,
      scale: 1.1
    };
    $[51] = t36;
  } else {
    t36 = $[51];
  }
  let t37;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <motion.div className="size-10 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center text-[hsl(var(--accent))] transition-colors duration-300 group-hover:bg-[hsl(var(--accent))]/15" whileHover={t36} transition={springHover}><GlobeIcon className="size-5" /></motion.div>;
    $[52] = t37;
  } else {
    t37 = $[52];
  }
  let t38;
  if ($[53] !== t) {
    t38 = t("sections.dev.web.title" as any);
    $[53] = t;
    $[54] = t38;
  } else {
    t38 = $[54];
  }
  let t39;
  if ($[55] !== t38) {
    t39 = <div className="flex items-center gap-3 mb-3">{t37}<h3 className="font-semibold text-foreground text-lg">{t38}</h3></div>;
    $[55] = t38;
    $[56] = t39;
  } else {
    t39 = $[56];
  }
  let t40;
  if ($[57] !== t) {
    t40 = t("sections.dev.web.description" as any);
    $[57] = t;
    $[58] = t40;
  } else {
    t40 = $[58];
  }
  let t41;
  if ($[59] !== t40) {
    t41 = <p className="text-sm text-muted-foreground leading-relaxed">{t40}</p>;
    $[59] = t40;
    $[60] = t41;
  } else {
    t41 = $[60];
  }
  let t42;
  if ($[61] !== t39 || $[62] !== t41) {
    t42 = <motion.div initial={t31} whileInView={t32} viewport={t33} transition={t34} whileHover={t35} className="group bg-card border border-border rounded-xl p-6 transition-all duration-300 hover:border-[hsl(var(--accent))]/20 hover:glow-accent">{t39}{t41}</motion.div>;
    $[61] = t39;
    $[62] = t41;
    $[63] = t42;
  } else {
    t42 = $[63];
  }
  let t43;
  if ($[64] !== t30 || $[65] !== t42) {
    t43 = <div className="mt-8 flex flex-col gap-5">{t30}{t42}</div>;
    $[64] = t30;
    $[65] = t42;
    $[66] = t43;
  } else {
    t43 = $[66];
  }
  let t44;
  if ($[67] !== t18 || $[68] !== t43) {
    t44 = <div>{t18}{t43}</div>;
    $[67] = t18;
    $[68] = t43;
    $[69] = t44;
  } else {
    t44 = $[69];
  }
  let t45;
  if ($[70] !== t12 || $[71] !== t44) {
    t45 = <section id="about" className="py-24 md:py-32 relative overflow-hidden">{t2}{t3}<div className="mx-auto max-w-6xl px-6 relative"><div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">{t12}{t44}</div></div></section>;
    $[70] = t12;
    $[71] = t44;
    $[72] = t45;
  } else {
    t45 = $[72];
  }
  let t46;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <div className="absolute top-0 left-0 right-0 section-divider" />;
    $[73] = t46;
  } else {
    t46 = $[73];
  }
  let t47;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <motion.div className="absolute -left-40 top-20 size-80 rounded-full bg-[hsl(var(--accent))]/4 blur-[100px]" animate={{
      scale: [1, 1.3, 1],
      x: [0, 20, 0]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[74] = t47;
  } else {
    t47 = $[74];
  }
  let t48;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <motion.div className="absolute -right-40 bottom-20 size-96 rounded-full bg-[hsl(var(--accent))]/4 blur-[100px]" animate={{
      scale: [1.2, 1, 1.2],
      x: [0, -20, 0]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[75] = t48;
  } else {
    t48 = $[75];
  }
  let t49;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <div className="absolute inset-0 pointer-events-none overflow-hidden"><svg className="absolute w-full h-full text-foreground opacity-[0.03]" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice"><circle cx="60" cy="120" r="3" fill="currentColor" /><circle cx="130" cy="220" r="2.5" fill="currentColor" /><circle cx="90" cy="370" r="3.5" fill="currentColor" /><circle cx="190" cy="170" r="2" fill="currentColor" /><circle cx="160" cy="420" r="3" fill="currentColor" /><line x1="60" y1="120" x2="130" y2="220" stroke="currentColor" strokeWidth="0.8" /><line x1="130" y1="220" x2="90" y2="370" stroke="currentColor" strokeWidth="0.8" /><line x1="60" y1="120" x2="190" y2="170" stroke="currentColor" strokeWidth="0.8" /><line x1="90" y1="370" x2="160" y2="420" stroke="currentColor" strokeWidth="0.8" /><line x1="130" y1="220" x2="160" y2="420" stroke="currentColor" strokeWidth="0.5" /><circle cx="1050" cy="150" r="3" fill="currentColor" /><circle cx="1110" cy="310" r="2.5" fill="currentColor" /><circle cx="1150" cy="210" r="2" fill="currentColor" /><circle cx="1010" cy="420" r="3.5" fill="currentColor" /><circle cx="1090" cy="520" r="3" fill="currentColor" /><line x1="1050" y1="150" x2="1110" y2="310" stroke="currentColor" strokeWidth="0.8" /><line x1="1050" y1="150" x2="1150" y2="210" stroke="currentColor" strokeWidth="0.8" /><line x1="1110" y1="310" x2="1010" y2="420" stroke="currentColor" strokeWidth="0.8" /><line x1="1010" y1="420" x2="1090" y2="520" stroke="currentColor" strokeWidth="0.8" /><line x1="1110" y1="310" x2="1090" y2="520" stroke="currentColor" strokeWidth="0.5" /><circle cx="400" cy="60" r="2" fill="currentColor" /><circle cx="600" cy="740" r="2.5" fill="currentColor" /><circle cx="800" cy="80" r="2" fill="currentColor" /><circle cx="500" cy="690" r="2" fill="currentColor" /></svg></div>;
    $[76] = t49;
  } else {
    t49 = $[76];
  }
  let t50;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <motion.div className="absolute left-[10%] top-[15%] size-2 rounded-full bg-[hsl(var(--accent))]/20" animate={{
      y: [0, -15, 0],
      opacity: [0.3, 0.7, 0.3]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[77] = t50;
  } else {
    t50 = $[77];
  }
  let t51;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <motion.div className="absolute right-[15%] top-[25%] size-1.5 rounded-full bg-[hsl(var(--accent))]/20" animate={{
      y: [0, 10, 0],
      opacity: [0.2, 0.6, 0.2]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 1
    }} />;
    $[78] = t51;
  } else {
    t51 = $[78];
  }
  let t52;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <motion.div className="absolute left-[20%] bottom-[20%] size-2 rounded-full bg-[hsl(var(--accent))]/15" animate={{
      y: [0, -10, 0],
      opacity: [0.2, 0.5, 0.2]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 2
    }} />;
    $[79] = t52;
  } else {
    t52 = $[79];
  }
  let t53;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <motion.div className="absolute right-[10%] bottom-[30%] size-1.5 rounded-full bg-[hsl(var(--accent))]/20" animate={{
      y: [0, 12, 0],
      opacity: [0.3, 0.6, 0.3]
    }} transition={{
      duration: 3.5,
      repeat: Infinity,
      ease: "easeInOut",
      delay: 0.5
    }} />;
    $[80] = t53;
  } else {
    t53 = $[80];
  }
  let t54;
  let t55;
  let t56;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = {
      translateY: 40,
      opacity: 0
    };
    t55 = {
      translateY: 0,
      opacity: 1
    };
    t56 = {
      once: true,
      margin: "-80px"
    };
    $[81] = t54;
    $[82] = t55;
    $[83] = t56;
  } else {
    t54 = $[81];
    t55 = $[82];
    t56 = $[83];
  }
  let t57;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = {
      duration: 0.7,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[84] = t57;
  } else {
    t57 = $[84];
  }
  let t58;
  if ($[85] !== t) {
    t58 = t("sections.ai.title");
    $[85] = t;
    $[86] = t58;
  } else {
    t58 = $[86];
  }
  let t59;
  if ($[87] !== t58) {
    t59 = <SectionLabel centered={true}>{t58}</SectionLabel>;
    $[87] = t58;
    $[88] = t59;
  } else {
    t59 = $[88];
  }
  let t60;
  let t61;
  let t62;
  let t63;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = {
      opacity: 0,
      translateY: 10
    };
    t61 = {
      opacity: 1,
      translateY: 0
    };
    t62 = {
      once: true
    };
    t63 = {
      delay: 0.2,
      duration: 0.6,
      ease: "easeOut"
    };
    $[89] = t60;
    $[90] = t61;
    $[91] = t62;
    $[92] = t63;
  } else {
    t60 = $[89];
    t61 = $[90];
    t62 = $[91];
    t63 = $[92];
  }
  let t64;
  if ($[93] !== t) {
    t64 = t("sections.ai.description");
    $[93] = t;
    $[94] = t64;
  } else {
    t64 = $[94];
  }
  let t65;
  if ($[95] !== t64) {
    t65 = <motion.p initial={t60} whileInView={t61} viewport={t62} transition={t63} className="mt-5 text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">{t64}</motion.p>;
    $[95] = t64;
    $[96] = t65;
  } else {
    t65 = $[96];
  }
  let t66;
  if ($[97] !== t59 || $[98] !== t65) {
    t66 = <motion.div initial={t54} whileInView={t55} viewport={t56} transition={t57} className="text-center mb-16">{t59}{t65}</motion.div>;
    $[97] = t59;
    $[98] = t65;
    $[99] = t66;
  } else {
    t66 = $[99];
  }
  let t67;
  if ($[100] !== t) {
    t67 = aiExamples.map((example, index) => <motion.div key={example.key} initial={{
      translateY: 30,
      opacity: 0
    }} whileInView={{
      translateY: 0,
      opacity: 1
    }} viewport={{
      once: true,
      margin: "-30px"
    }} transition={{
      duration: 0.5,
      delay: index * 0.08,
      ease: "easeOut"
    }} whileHover={{
      translateY: -5
    }} className="group flex flex-col gap-4 bg-card/80 backdrop-blur-sm border border-border rounded-xl p-6 transition-all duration-300 hover:border-[hsl(var(--accent))]/20 hover:glow-accent"><motion.div className="size-11 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center text-[hsl(var(--accent))] transition-colors duration-300 group-hover:bg-[hsl(var(--accent))]/15" whileHover={{
        rotate: 8,
        scale: 1.15
      }} transition={springHover}><example.icon className="size-5" /></motion.div><h3 className="font-semibold text-foreground text-base">{t(`sections.ai.examples.${example.key}.title` as any)}</h3><p className="text-sm text-muted-foreground leading-relaxed">{t(`sections.ai.examples.${example.key}.description` as any)}</p></motion.div>);
    $[100] = t;
    $[101] = t67;
  } else {
    t67 = $[101];
  }
  let t68;
  if ($[102] !== t67) {
    t68 = <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">{t67}</div>;
    $[102] = t67;
    $[103] = t68;
  } else {
    t68 = $[103];
  }
  let t69;
  if ($[104] !== t66 || $[105] !== t68) {
    t69 = <section id="ai" className="py-24 md:py-32 relative overflow-hidden">{t46}{t47}{t48}{t49}{t50}{t51}{t52}{t53}<div className="mx-auto max-w-6xl px-6 relative">{t66}{t68}</div></section>;
    $[104] = t66;
    $[105] = t68;
    $[106] = t69;
  } else {
    t69 = $[106];
  }
  let t70;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <div className="absolute top-0 left-0 right-0 section-divider" />;
    $[107] = t70;
  } else {
    t70 = $[107];
  }
  let t71;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <motion.div className="absolute -left-40 bottom-0 size-80 rounded-full bg-[hsl(var(--accent))]/3 blur-[120px]" animate={{
      scale: [1, 1.15, 1]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[108] = t71;
  } else {
    t71 = $[108];
  }
  let t72;
  let t73;
  let t74;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = {
      translateY: 40,
      opacity: 0
    };
    t73 = {
      translateY: 0,
      opacity: 1
    };
    t74 = {
      once: true,
      margin: "-80px"
    };
    $[109] = t72;
    $[110] = t73;
    $[111] = t74;
  } else {
    t72 = $[109];
    t73 = $[110];
    t74 = $[111];
  }
  let t75;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = {
      duration: 0.7,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[112] = t75;
  } else {
    t75 = $[112];
  }
  let t76;
  if ($[113] !== t) {
    t76 = t("sections.projects.title");
    $[113] = t;
    $[114] = t76;
  } else {
    t76 = $[114];
  }
  let t77;
  if ($[115] !== t76) {
    t77 = <motion.div initial={t72} whileInView={t73} viewport={t74} transition={t75} className="text-center mb-14"><SectionLabel centered={true}>{t76}</SectionLabel></motion.div>;
    $[115] = t76;
    $[116] = t77;
  } else {
    t77 = $[116];
  }
  let t78;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t78 = <Projects />;
    $[117] = t78;
  } else {
    t78 = $[117];
  }
  let t79;
  if ($[118] !== t77) {
    t79 = <section id="projects" className="py-24 md:py-32 relative overflow-hidden">{t70}{t71}<div className="mx-auto max-w-6xl px-6 relative">{t77}{t78}</div></section>;
    $[118] = t77;
    $[119] = t79;
  } else {
    t79 = $[119];
  }
  let t80;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t80 = <div className="absolute top-0 left-0 right-0 section-divider" />;
    $[120] = t80;
  } else {
    t80 = $[120];
  }
  let t81;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = <motion.div className="absolute right-0 top-1/2 -translate-y-1/2 size-96 rounded-full bg-[hsl(var(--accent))]/3 blur-[140px]" animate={{
      scale: [1, 1.2, 1],
      x: [0, -10, 0]
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[121] = t81;
  } else {
    t81 = $[121];
  }
  let t82;
  let t83;
  let t84;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = {
      translateY: 40,
      opacity: 0
    };
    t83 = {
      translateY: 0,
      opacity: 1
    };
    t84 = {
      once: true,
      margin: "-80px"
    };
    $[122] = t82;
    $[123] = t83;
    $[124] = t84;
  } else {
    t82 = $[122];
    t83 = $[123];
    t84 = $[124];
  }
  let t85;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = {
      duration: 0.7,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[125] = t85;
  } else {
    t85 = $[125];
  }
  let t86;
  if ($[126] !== t) {
    t86 = t("sections.testimonials.title");
    $[126] = t;
    $[127] = t86;
  } else {
    t86 = $[127];
  }
  let t87;
  if ($[128] !== t86) {
    t87 = <motion.div initial={t82} whileInView={t83} viewport={t84} transition={t85} className="text-center mb-14"><SectionLabel centered={true}>{t86}</SectionLabel></motion.div>;
    $[128] = t86;
    $[129] = t87;
  } else {
    t87 = $[129];
  }
  let t88;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <Testimonials />;
    $[130] = t88;
  } else {
    t88 = $[130];
  }
  let t89;
  if ($[131] !== t87) {
    t89 = <section id="testimonials" className="py-24 md:py-32 relative overflow-hidden">{t80}{t81}<div className="mx-auto max-w-6xl px-6 relative">{t87}{t88}</div></section>;
    $[131] = t87;
    $[132] = t89;
  } else {
    t89 = $[132];
  }
  let t90;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = <div className="absolute top-0 left-0 right-0 section-divider" />;
    $[133] = t90;
  } else {
    t90 = $[133];
  }
  let t91;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t91 = <motion.div className="absolute left-1/2 -translate-x-1/2 top-0 w-150 h-75 rounded-full bg-[hsl(var(--accent))]/5 blur-[120px]" animate={{
      scale: [1, 1.1, 1],
      opacity: [0.7, 1, 0.7]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: "easeInOut"
    }} />;
    $[134] = t91;
  } else {
    t91 = $[134];
  }
  let t92;
  let t93;
  let t94;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = {
      translateY: 40,
      opacity: 0
    };
    t93 = {
      translateY: 0,
      opacity: 1
    };
    t94 = {
      once: true,
      margin: "-80px"
    };
    $[135] = t92;
    $[136] = t93;
    $[137] = t94;
  } else {
    t92 = $[135];
    t93 = $[136];
    t94 = $[137];
  }
  let t95;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t95 = {
      duration: 0.7,
      ease: [0.25, 0.1, 0.25, 1]
    };
    $[138] = t95;
  } else {
    t95 = $[138];
  }
  let t96;
  if ($[139] !== t) {
    t96 = t("sections.contactForm.title");
    $[139] = t;
    $[140] = t96;
  } else {
    t96 = $[140];
  }
  let t97;
  if ($[141] !== t96) {
    t97 = <SectionLabel centered={true}>{t96}</SectionLabel>;
    $[141] = t96;
    $[142] = t97;
  } else {
    t97 = $[142];
  }
  let t100;
  let t101;
  let t98;
  let t99;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t98 = {
      opacity: 0,
      translateY: 10
    };
    t99 = {
      opacity: 1,
      translateY: 0
    };
    t100 = {
      once: true
    };
    t101 = {
      delay: 0.15,
      duration: 0.6,
      ease: "easeOut"
    };
    $[143] = t100;
    $[144] = t101;
    $[145] = t98;
    $[146] = t99;
  } else {
    t100 = $[143];
    t101 = $[144];
    t98 = $[145];
    t99 = $[146];
  }
  let t102;
  if ($[147] !== t) {
    t102 = t("sections.contactForm.description");
    $[147] = t;
    $[148] = t102;
  } else {
    t102 = $[148];
  }
  let t103;
  if ($[149] !== t102) {
    t103 = <motion.p initial={t98} whileInView={t99} viewport={t100} transition={t101} className="mt-5 text-muted-foreground text-lg max-w-lg mx-auto leading-relaxed">{t102}</motion.p>;
    $[149] = t102;
    $[150] = t103;
  } else {
    t103 = $[150];
  }
  let t104;
  let t105;
  let t106;
  let t107;
  if ($[151] === Symbol.for("react.memo_cache_sentinel")) {
    t104 = {
      opacity: 0,
      translateY: 15
    };
    t105 = {
      opacity: 1,
      translateY: 0
    };
    t106 = {
      once: true
    };
    t107 = {
      delay: 0.3,
      duration: 0.6,
      ease: "easeOut"
    };
    $[151] = t104;
    $[152] = t105;
    $[153] = t106;
    $[154] = t107;
  } else {
    t104 = $[151];
    t105 = $[152];
    t106 = $[153];
    t107 = $[154];
  }
  let t108;
  if ($[155] !== t) {
    t108 = t("sections.contactForm.title");
    $[155] = t;
    $[156] = t108;
  } else {
    t108 = $[156];
  }
  let t109;
  if ($[157] !== t) {
    t109 = t("sections.contactForm.description");
    $[157] = t;
    $[158] = t109;
  } else {
    t109 = $[158];
  }
  let t110;
  let t111;
  let t112;
  if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
    t110 = {
      scale: 1.04
    };
    t111 = {
      scale: 0.97
    };
    t112 = {
      type: "spring",
      stiffness: 400,
      damping: 17
    };
    $[159] = t110;
    $[160] = t111;
    $[161] = t112;
  } else {
    t110 = $[159];
    t111 = $[160];
    t112 = $[161];
  }
  let t113;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t113 = <MailIcon className="size-4" />;
    $[162] = t113;
  } else {
    t113 = $[162];
  }
  let t114;
  if ($[163] !== t) {
    t114 = t("buttons.getInTouch");
    $[163] = t;
    $[164] = t114;
  } else {
    t114 = $[164];
  }
  let t115;
  if ($[165] !== t114) {
    t115 = <motion.div whileHover={t110} whileTap={t111} transition={t112}><Button type="button" size="lg" className="h-12 px-6 text-sm font-semibold shadow-lg shadow-[hsl(var(--accent))]/10">{t113}{t114}</Button></motion.div>;
    $[165] = t114;
    $[166] = t115;
  } else {
    t115 = $[166];
  }
  let t116;
  if ($[167] === Symbol.for("react.memo_cache_sentinel")) {
    t116 = <ContactForm />;
    $[167] = t116;
  } else {
    t116 = $[167];
  }
  let t117;
  if ($[168] !== t108 || $[169] !== t109 || $[170] !== t115) {
    t117 = <Modal title={t108} description={t109} trigger={t115}>{t116}</Modal>;
    $[168] = t108;
    $[169] = t109;
    $[170] = t115;
    $[171] = t117;
  } else {
    t117 = $[171];
  }
  let t118;
  let t119;
  let t120;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t118 = {
      scale: 1.04
    };
    t119 = {
      scale: 0.97
    };
    t120 = {
      type: "spring",
      stiffness: 400,
      damping: 17
    };
    $[172] = t118;
    $[173] = t119;
    $[174] = t120;
  } else {
    t118 = $[172];
    t119 = $[173];
    t120 = $[174];
  }
  let t121;
  if ($[175] === Symbol.for("react.memo_cache_sentinel")) {
    t121 = <CalendarIcon className="size-4" />;
    $[175] = t121;
  } else {
    t121 = $[175];
  }
  let t122;
  if ($[176] !== t) {
    t122 = t("buttons.bookACall");
    $[176] = t;
    $[177] = t122;
  } else {
    t122 = $[177];
  }
  let t123;
  if ($[178] !== t122) {
    t123 = <Link href="https://calendly.com/alaindifabio/discovery-call" target="_blank"><motion.div whileHover={t118} whileTap={t119} transition={t120}><Button type="button" variant="outline" size="lg" className="h-12 px-6 text-sm font-semibold">{t121}{t122}</Button></motion.div></Link>;
    $[178] = t122;
    $[179] = t123;
  } else {
    t123 = $[179];
  }
  let t124;
  if ($[180] !== t117 || $[181] !== t123) {
    t124 = <motion.div initial={t104} whileInView={t105} viewport={t106} transition={t107} className="flex flex-wrap items-center justify-center gap-4 mt-10">{t117}{t123}</motion.div>;
    $[180] = t117;
    $[181] = t123;
    $[182] = t124;
  } else {
    t124 = $[182];
  }
  let t125;
  if ($[183] !== t103 || $[184] !== t124 || $[185] !== t97) {
    t125 = <section id="contact" className="py-24 md:py-32 relative overflow-hidden">{t90}{t91}<motion.div initial={t92} whileInView={t93} viewport={t94} transition={t95} className="mx-auto max-w-6xl px-6 text-center relative">{t97}{t103}{t124}</motion.div></section>;
    $[183] = t103;
    $[184] = t124;
    $[185] = t97;
    $[186] = t125;
  } else {
    t125 = $[186];
  }
  let t126;
  if ($[187] !== t125 || $[188] !== t45 || $[189] !== t69 || $[190] !== t79 || $[191] !== t89) {
    t126 = <main>{t1}{t45}{t69}{t79}{t89}{t125}</main>;
    $[187] = t125;
    $[188] = t45;
    $[189] = t69;
    $[190] = t79;
    $[191] = t89;
    $[192] = t126;
  } else {
    t126 = $[192];
  }
  let t127;
  if ($[193] === Symbol.for("react.memo_cache_sentinel")) {
    t127 = <Footer />;
    $[193] = t127;
  } else {
    t127 = $[193];
  }
  let t128;
  if ($[194] !== t126) {
    t128 = <div className="min-h-screen flex flex-col bg-background">{t0}{t126}{t127}</div>;
    $[194] = t126;
    $[195] = t128;
  } else {
    t128 = $[195];
  }
  return t128;
}
function _temp2(children_0) {
  return <p>{children_0}</p>;
}
function _temp(children) {
  return <strong className="font-semibold text-foreground">{children}</strong>;
}
function SectionLabel(t0) {
  const $ = _c(19);
  const {
    children,
    centered
  } = t0;
  const t1 = `flex items-center gap-3 ${centered ? "justify-center" : ""}`;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      scaleX: 0
    };
    t3 = {
      scaleX: 1
    };
    t4 = {
      once: true
    };
    t5 = {
      duration: 0.5,
      ease: "easeOut"
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  const t6 = centered ? "center" : "left";
  let t7;
  if ($[4] !== t6) {
    t7 = <motion.div className="w-8 h-px bg-[hsl(var(--accent))]" initial={t2} whileInView={t3} viewport={t4} transition={t5} style={{
      transformOrigin: t6
    }} />;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0,
      translateY: 5
    };
    t9 = {
      opacity: 1,
      translateY: 0
    };
    t10 = {
      once: true
    };
    t11 = {
      duration: 0.5,
      delay: 0.1,
      ease: "easeOut"
    };
    $[6] = t10;
    $[7] = t11;
    $[8] = t8;
    $[9] = t9;
  } else {
    t10 = $[6];
    t11 = $[7];
    t8 = $[8];
    t9 = $[9];
  }
  let t12;
  if ($[10] !== children) {
    t12 = <motion.h2 initial={t8} whileInView={t9} viewport={t10} transition={t11} className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--accent))] font-mono">{children}</motion.h2>;
    $[10] = children;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== centered) {
    t13 = centered && <motion.div className="w-8 h-px bg-[hsl(var(--accent))]" initial={{
      scaleX: 0
    }} whileInView={{
      scaleX: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      ease: "easeOut"
    }} style={{
      transformOrigin: "center"
    }} />;
    $[12] = centered;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== t1 || $[15] !== t12 || $[16] !== t13 || $[17] !== t7) {
    t14 = <div className={t1}>{t7}{t12}{t13}</div>;
    $[14] = t1;
    $[15] = t12;
    $[16] = t13;
    $[17] = t7;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  return t14;
}